import { GuardiaRow } from "../../../../components/common/PL/Table/GuardiaTable";
import {
  driverName,
  eventUserId,
  VehicleInsideRow,
} from "../../../../components/common/PL/Table/utils/vehicleInsideRow";
import type { VehicleInsideRole } from "../../../../passapp-sdk/types/userTypes";
import { PAIS_ARGENTINA_ID } from "../../../../utils/constants/locationConstants";
import { RegistroIngresoPrefill } from "../registroIngresoNavigation";
import { mapGuardiaToPrefill } from "./prefillUtils";
import { toTrailerVehicleFormValues } from "./trailerVehicleForm";

/** Quién retira las unidades: el chofer que las ingresó, u otra persona cargada por documento. */
export type ExitResponsable =
  | { tipo: "chofer" }
  | {
      tipo: "otro";
      documentCountry: string;
      documentNumber: string;
      /** Ficha devuelta por `findUserByDNI`, si el documento ya estaba en el sistema. */
      user?: any;
    };

type EventVehicleLike = { id?: string; plate?: string };

/**
 * Unidad elegida en el modal de salida.
 *
 * El rol define cuál es el vehículo principal del egreso, y `row` —cuando la unidad está en el
 * listado— es de dónde salen su id y su ficha. Hace falta porque el tractor que viene a retirar un
 * acoplado suele ser de **otro** ingreso: buscarlo en el evento del acoplado no lo encuentra.
 * Sin `row` la unidad es externa al complejo y sólo tenemos la patente.
 */
export type ExitUnitSelection = {
  plate: string;
  role: VehicleInsideRole;
  row?: VehicleInsideRow;
};

/** Vehículos de un evento indexados por patente: de ahí salen los ids reales de cada unidad. */
const vehiclesByPlate = (event: any): Map<string, EventVehicleLike> => {
  const byPlate = new Map<string, EventVehicleLike>();
  const add = (vehicle: any) => {
    if (vehicle && typeof vehicle !== "string" && vehicle.plate) {
      byPlate.set(String(vehicle.plate).toUpperCase(), vehicle);
    }
  };
  add(event?.vehicle);
  (event?.trailers ?? []).forEach(add);
  return byPlate;
};

/**
 * Arma el prefill del formulario de egreso a partir de lo elegido en el modal de salida.
 *
 * El backend no permite dar salida a un vehículo sin persona (`addEvent` siempre lleva `who`), así
 * que el modal es sólo un paso de selección: acá se traduce la selección al prefill del formulario
 * de egreso, que es el que finalmente hace el `addEvent`.
 *
 * La unidad principal del egreso es el tractor —un acoplado no sale solo— y el resto viaja en
 * `trailerVehicles`. Los ids se resuelven contra el evento para que `resolveTrailerVehicles` no
 * vuelva a crear vehículos que ya existen.
 */
export const buildExitPrefill = (
  responsable: ExitResponsable,
  row: VehicleInsideRow,
  selected: ExitUnitSelection[]
): RegistroIngresoPrefill => {
  const base: RegistroIngresoPrefill =
    responsable.tipo === "chofer" ? buildDriverPrefill(row) : buildOtherPersonPrefill(responsable);

  // Si el guardia destildó todo queda la unidad sobre la que se abrió el modal: por acá siempre
  // sale al menos una, un egreso sin unidades es cosa del modal de persona.
  return applyUnitSelection(base, selected, row.event, {
    plate: row.plate,
    role: row.role,
    row,
  });
};

/**
 * Traduce las unidades elegidas a los campos de vehículo del prefill.
 *
 * Lo comparten los dos modales de salida —el de la unidad y el de la persona—, que difieren sólo en
 * de dónde sale la persona del egreso.
 *
 * @param eventoDelModal evento sobre el que se abrió el modal, plan B para resolver los ids.
 * @param fallback unidad a usar cuando la selección quedó vacía; sin ella, salida a pie.
 */
const applyUnitSelection = (
  base: RegistroIngresoPrefill,
  selected: ExitUnitSelection[],
  eventoDelModal?: any,
  fallback?: ExitUnitSelection
): RegistroIngresoPrefill => {
  const byPlate = vehiclesByPlate(eventoDelModal);

  /**
   * Ficha de la unidad: primero el evento de su propia fila —el tractor que retira puede venir de
   * otro ingreso—, y como plan B el evento sobre el que se abrió el modal, que cubre a las
   * enganchadas. Si no está en ninguno, es externa y sólo queda la patente.
   */
  const lookup = (unit?: ExitUnitSelection): EventVehicleLike | undefined => {
    if (!unit) return undefined;
    const propio = vehiclesByPlate(unit.row?.event).get(unit.plate.toUpperCase());
    return propio ?? byPlate.get(unit.plate.toUpperCase());
  };

  // El tractor manda como vehículo principal: un acoplado no puede ser el vehículo de un egreso.
  const principalUnit = selected.find((unit) => unit.role === "tractor") ?? selected[0] ?? fallback;
  const principalPlate = principalUnit?.plate;
  const principal = lookup(principalUnit);

  return {
    ...base,
    // Cadena vacía y no `undefined`: para el formulario de transporte "sin patente" es una decisión
    // del guardia, no un dato que falta (ver `resolveExitVehiclePrefill`).
    vehiclePlate: principalPlate ?? "",
    // `vehicleType` acá es el medio de acceso del formulario ("none" | "car" | "bike"), no la
    // carrocería: si sale con unidades, siempre es "car".
    vehicleType: principalPlate ? "car" : "none",
    vehicleBrand: (principal as any)?.brand ?? base.vehicleBrand,
    vehicleModel: (principal as any)?.model ?? base.vehicleModel,
    vehicleColor: (principal as any)?.color ?? base.vehicleColor,
    // La ficha completa se conserva hasta que `getVehicles` devuelva la lista del chofer. Reducirla
    // a patente/marca en este punto hacía que el formulario de salida perdiera seguro, VTV y fotos.
    exitVehicle: principal,
    // El sentinel "exit-vehicle-id" es el que ya usa `mapGuardiaToPrefill` cuando sólo hay patente.
    selectedVehicleId: principalPlate ? principal?.id ?? "exit-vehicle-id" : null,
    // La selección del modal es final: sin esto, el formulario de transporte vuelve a sembrar el
    // acoplado del ingreso cuando el guardia lo destildó y la lista queda vacía.
    exitUnitsPreselected: true,
    // Igual que `principal` arriba: la ficha completa de la unidad (marca, VTV, seguro) sale del
    // vehículo resuelto por `lookup`, no sólo su id — si no, el acoplado llega al formulario con
    // sólo la patente y el guardia tiene que volver a tipear todo (ver TransportContentForm, que
    // hace el mismo mapeo para el acoplado sembrado cuando el egreso no vino del modal de salida).
    trailerVehicles: selected
      .filter((unit) => unit.plate.toUpperCase() !== (principalPlate ?? "").toUpperCase())
      .map((unit) => toTrailerVehicleFormValues(lookup(unit), { vehiclePlate: unit.plate })),
  };
};

/**
 * Prefill del egreso armado desde la vista **por persona**.
 *
 * La persona ya está identificada —es la fila sobre la que el guardia apretó Salida—, así que la
 * base sale de `mapGuardiaToPrefill`, que trae más que `buildDriverPrefill`: mail, teléfono,
 * nacimiento, fotos y legajo del chofer.
 *
 * Con `selected` vacío la persona sale **a pie**: las unidades quedan dentro sin chofer y el evento
 * no lleva ningún vehículo.
 */
export const buildPersonExitPrefill = (
  guardia: GuardiaRow,
  selected: ExitUnitSelection[]
): RegistroIngresoPrefill => {
  const base = mapGuardiaToPrefill(guardia);

  if (selected.length === 0) {
    return {
      ...base,
      vehiclePlate: "",
      vehicleType: "none",
      vehicleBrand: undefined,
      vehicleModel: undefined,
      vehicleColor: undefined,
      selectedVehicleId: null,
      exitUnitsPreselected: true,
      trailerVehicles: [],
      // El medio de acceso no alcanza: en transporte se fuerza a "car" y la patente se recupera de
      // la autorización de egreso. Este flag es el que corta esos dos caminos.
      exitOnFoot: true,
    };
  }

  return applyUnitSelection(base, selected, guardia.event);
};

/** El chofer que ingresó: se cierra su propio evento de entrada. */
const buildDriverPrefill = (row: VehicleInsideRow): RegistroIngresoPrefill => {
  const nombre = driverName(row.driver) ?? "";
  const [firstName = "", ...rest] = nombre.split(" ");
  return {
    id: eventUserId(row),
    eventId: row.eventId,
    // TODO: quitar el default cuando getVehiclesInside devuelva `driver.countryCode`.
    documentCountry: row.driver?.countryCode ?? PAIS_ARGENTINA_ID,
    documentNumber: row.driver?.dni,
    firstName: row.driver?.firstname ?? firstName,
    lastName: row.driver?.lastname ?? rest.join(" "),
    inEventComment: row.event?.comment,
    inEventPeopleComment: row.event?.peopleComment,
    entranceEvent: row.event,
  };
};

/**
 * Quien retira puede ser alguien de afuera del complejo. Si `findUserByDNI` lo encontró se
 * aprovechan sus datos; si no, alcanza con país y documento y el alta ocurre en el formulario,
 * igual que en "Registrar salida sin check-in".
 */
const buildOtherPersonPrefill = (responsable: ExitResponsable): RegistroIngresoPrefill => {
  if (responsable.tipo !== "otro") return {};
  const { user, documentCountry, documentNumber } = responsable;
  return {
    id: user?.id,
    documentCountry,
    documentNumber,
    firstName: user?.firstname ?? "",
    lastName: user?.lastname ?? "",
    email: user?.email,
    phone: user?.phone,
    visitorPhoto: user?.facePhoto,
    frontDNI: user?.frontDNI,
    backDNI: user?.backDNI,
  };
};
